import './LoginDialog.less';
import LoginPassHidden from 'nike-ui/src/icons/svg/login-pass-hidden-sv.svg';
import LoginPassShowed from 'nike-ui/src/icons/svg/login-pass-showed-sv.svg';
import NikeLogoIcon from '@/icons/NikeLogoIcon';
import ReCaptchaLazy from 'react-google-recaptcha';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import clsx from 'clsx';
import i18n from '@i18n';
import useLocalStorage from 'nike-ui/src/common/hooks/useLocalStorage';
import { BackButton, CrossButton } from '@/common/components/HelperButtons';
import { Box } from '@/common/components/layout/Box';
import { Button } from 'nike-ui/src/components/button/Button';
import { CONTACT_US, REGISTRATION, RESET_PASSWORD } from '@/routing/routes';
import { DialogBody, DialogTitle, Fullscreen } from '@/ui/dialogs/Dialog';
import { catcher, toWwwForm } from '@/_http/utils';
import { dispatch, useReframe } from 'nike-re-framejs';
import { fireGAEvent } from '@/analytics';
import { getConfigProperty, getProperty, getUrl } from '@/common/oldMobile';
import { loggedIn$ } from '@/account/subs';
import { navigate } from '@/routing';
import { registrationQueryParams } from 'nike-ui/src/components/dwh-tracking/api';
import { replacePlaceholdersInString } from '@/common/replacePlaceholdersInString';
import { sendUsingForm } from '@/common/client';
import { startLogin2FA } from '@/loginng/handlers';
import { useLocation } from 'react-router-dom';

export const LoginPage = () => {
    const { search } = useLocation();
    const query = useMemo(() => new URLSearchParams(search), [search]);
    return (
        <Box padding={20}>
            <LoginForm onboardingMsg={query.get('p') ? i18n('registration.email.verified') : null} />
        </Box>
    );
};

export function LoginForm({ registerInfo = true, loginOrigin, onboardingMsg }) {
    const { subscribe } = useReframe();
    const loggedIn = subscribe(loggedIn$);
    const [dark] = useLocalStorage('themeDark');
    const loginFormRef = useRef(null);
    const [login, setLogin] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [password, setPassword] = useState('');
    const [recaptchaToken, setRecaptchaToken] = useState(null);
    const [captchaRequired, setCaptchaRequired] = useState(false);
    const [recaptchaIdentity, setRecaptchaIdentity] = useState(1);

    useEffect(() => {
        if (loggedIn) {
            dispatch(['login.closeScreen']);
        }
    }, [loggedIn]);

    const [alert, setAlert] = useState(null);
    function handleSubmit(e) {
        fireGAEvent('Prihlasenie', 'Prihlasit sa', loginOrigin);
        const validLogin = login.length;
        const validPassword = password.length;
        if (validLogin && validPassword && captchaRequired === Boolean(recaptchaToken)) {
            doLoginAttempt();
        }
        e.preventDefault();
    }

    function doLoginAttempt() {
        const data = { userName: login, password: password, captchaText: recaptchaToken };
        setSubmitting(true);
        axios
            .post(getUrl('XHR_LOGIN'), toWwwForm(data))
            .then(response => {
                if (response.required2FA) {
                    return Promise.reject(response);
                }
                return response;
            })
            .then(({ alert }) => {
                if (alert) {
                    setAlert(alert);
                    if (alert.captchaRequired) {
                        setCaptchaRequired(true);
                    }
                    return Promise.reject();
                }
                loginFormRef.current.submit();
            })
            .catch(e => {
                if (e.required2FA) {
                    const onSuccess = token =>
                        doSimple2FALoginAttempt(login, password, token, window.location.pathname);
                    dispatch(startLogin2FA(e, onSuccess, () => setSubmitting(false)));
                    return Promise.resolve();
                }
                return Promise.reject(e);
            })
            .catch(e => {
                setRecaptchaToken(null);
                setRecaptchaIdentity(recaptchaIdentity + 1);
                setSubmitting(false);
                return catcher.swallow(e);
            });
    }

    const loginLabel = i18n('jsp.profil.label.PrihlasovacieMeno');
    const passwordLabel = i18n('jsp.profil.label.Heslo');
    const hasFieldError = !login.length || !password.length || !(captchaRequired === Boolean(recaptchaToken));

    return (
        <div className="login-form">
            {!alert ? (
                <div className={clsx({ 'login-nike-logo': alert })}>
                    <div className="nike-logo">
                        <NikeLogoIcon width={77} height={34} opacity={1} fill={'#ffffff'} />
                    </div>
                </div>
            ) : null}
            <LoginAlert alert={alert} />
            {onboardingMsg && <div className="onboard-msg">{onboardingMsg}</div>}
            <form className="login-form-" onSubmit={handleSubmit} method="post" action="" ref={loginFormRef}>
                <input
                    name="username"
                    autoComplete="username"
                    autoCapitalize="off"
                    placeholder={loginLabel}
                    className="field"
                    type="text"
                    onChange={e => setLogin(e.target.value)}
                    data-atid="login-userName-input"
                />
                <PasswordField
                    name="password"
                    autoComplete="current-password"
                    placeholder={passwordLabel}
                    className="field form-input-password"
                    type="password"
                    onChange={e => setPassword(e.target.value)}
                    data-atid="login-password-input"
                    loginOrigin={loginOrigin}
                />
                <input type="hidden" name="loginRequest" value="loginRequest" />
                <div className="login-links cf">
                    <a
                        className="left"
                        data-atid="login-password-forgotten"
                        onClick={e => {
                            fireGAEvent('Prihlasenie', 'Zabudol som heslo', loginOrigin);
                            openPage(e, RESET_PASSWORD);
                        }}
                    >
                        {i18n('commons.button.zabudnuteHeslo')}
                    </a>
                </div>
                {captchaRequired ? (
                    <ReCaptchaLazy
                        sitekey={getProperty('recaptcha.sitekey') ?? ''}
                        onChange={e => setRecaptchaToken(e)}
                        key={recaptchaIdentity}
                        theme={dark ? 'dark' : 'light'}
                    />
                ) : null}
                <Button
                    type="submit"
                    data-atid="login-submit-button"
                    disabled={hasFieldError || submitting}
                    label={i18n('jsp.login.Prihlasit')}
                    loading={submitting}
                    primary
                />
                <Button
                    type="submit"
                    data-atid="anonymous-reg-button"
                    onClick={e => {
                        fireGAEvent('Prihlasenie', 'Zaregistrovat sa', loginOrigin);
                        openPage(e, REGISTRATION, registrationQueryParams('LOGIN_DIALOG_BUTTON'));
                    }}
                    label={i18n('jsp.login.register')}
                />

                {registerInfo ? <LoginTroublesInfo /> : null}
            </form>
        </div>
    );
}

function doSimple2FALoginAttempt(userName, password, token2FA, path = '/') {
    return axios
        .post(getUrl('XHR_LOGIN'), toWwwForm({ userName, password, token2FA }))
        .then(response => {
            if (response.required2FA) {
                return Promise.reject('2FA while 2FA, eww!');
            }
            if (response.alert) {
                return Promise.reject('Login errors while 2FA, eww!');
            }
            return response;
        })
        .then(() => sendUsingForm(path, { loginRequest: 'loginRequest' }));
}

function openPage(e, route, query = {}) {
    navigate(route, null, Object.assign({ prihlasenie: undefined }, query));
    e.preventDefault();
    e.stopPropagation();
}

function LoginTroublesInfo() {
    const mail = getConfigProperty('mail.service.fromAddress');
    const componentReplaceMap = {
        '{0}': () => <a href={`mailto:${mail}`}>{mail}</a>,
        '{1}': () => <a onClick={e => openPage(e, CONTACT_US)}>{i18n('login.screen.contact-us-if-problem.link')}</a>
    };

    return (
        <div className="login-register-info login-trouble-info">
            <div className="login-register-text1">
                {replacePlaceholdersInString(i18n('login.screen.contact-us-if-problem.text'), componentReplaceMap)}
            </div>
        </div>
    );
}

function PasswordField({ children, loginOrigin, ...rest }) {
    const [showPassword, setShowPassword] = useState(false);

    function changeState(e) {
        e.stopPropagation();
        fireGAEvent('Prihlasenie', 'Ukaz heslo', loginOrigin);
        setShowPassword(!showPassword);
    }

    return (
        <div className="common-login-bar">
            <input {...rest} type={showPassword ? 'text' : 'password'} />
            <button type="button" className="toggle-password" data-atid="login-toogle-password" onClick={changeState}>
                {showPassword ? <LoginPassShowed /> : <LoginPassHidden />}
            </button>
        </div>
    );
}

function LoginAlert({ alert }) {
    if (!alert) {
        return null;
    }
    if (alert.blocked) {
        return (
            <div className="alert alert-login">
                <h4 className="icon">{i18n('jsp.login.errBlock.title')}</h4>
                <p>{i18n('jsp.login.errBlock.msg')}</p>
            </div>
        );
    }
    if (alert.message) {
        return (
            <div className={clsx('alert alert-login', { 'alert-login-password': alert.wrongPassword })}>
                <h4 className="icon">{i18n('jsp.login.err.title')}</h4>
                <p>{alert.message}</p>
            </div>
        );
    }

    return (
        <div className="alert alert-warn">
            <h4 className="icon">{i18n('jsp.login.err.title')}</h4>
            <p>{i18n('jsp.login.err.msg')}</p>
        </div>
    );
}

function LoginOverlay() {
    useLayoutEffect(() => {
        // iOS scroll fix (nie, fakt nechapem preco to tam safari musi mat 2x, ale bez toho to nefunguje)
        document.ontouchmove = e => {
            e.preventDefault();
        };
        document.ontouchmove = e => {
            e.preventDefault();
        };

        document.getElementById('page').classList.add('page-blur');

        return () => {
            document.ontouchmove = () => true;
            document.getElementById('page')?.classList.remove('page-blur');
        };
    }, []);

    function handleCloseClick() {
        fireGAEvent('Prihlasenie', 'Zatvorit prihlasovacie okno', 'Modal');
        dispatch(['login.closeScreen']);
    }
    return (
        <Fullscreen className="login-overlay" onClose={handleCloseClick}>
            <div className="dialog-wrap-rw">
                <LoginFormDialog handleCloseClick={handleCloseClick} />
            </div>
        </Fullscreen>
    );
}

function LoginFormDialog({ handleCloseClick }) {
    return (
        <>
            <DialogTitle>
                <BackButton button={<CrossButton />} onClick={handleCloseClick} data-atid="login-close" />
            </DialogTitle>
            <DialogBody>
                <LoginForm loginOrigin="Modal" />
            </DialogBody>
        </>
    );
}

export default LoginOverlay;
